import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface Setting {
  key: string;
  value: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export const DeliveryExclusionZonesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [editedZones, setEditedZones] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch delivery exclusion zones setting
  const { data: setting, isLoading } = useQuery<Setting>({
    queryKey: ['settings', 'delivery_exclusion_zones'],
    queryFn: async () => {
      const response = await apiClient.get('/settings/delivery_exclusion_zones');
      return response.data;
    },
  });

  // Update editedZones when setting data is loaded
  React.useEffect(() => {
    if (setting) {
      setEditedZones(setting.value);
    }
  }, [setting]);

  // Update setting mutation
  const updateMutation = useMutation({
    mutationFn: async (value: string) => {
      const response = await apiClient.put('/settings/delivery_exclusion_zones', {
        value,
        description: '차량 배달 서비스 제외 지역 (줄바꿈으로 구분)',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery_exclusion_zones'] });
      setHasChanges(false);
      alert('배달 제외지역이 저장되었습니다.');
    },
    onError: (error: any) => {
      alert(`저장 실패: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedZones(e.target.value);
    setHasChanges(e.target.value !== setting?.value);
  };

  const handleSave = () => {
    if (!hasChanges) {
      alert('변경사항이 없습니다.');
      return;
    }
    updateMutation.mutate(editedZones);
  };

  const handleReset = () => {
    if (setting) {
      setEditedZones(setting.value);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">로딩 중...</div>
        </div>
      </Layout>
    );
  }

  const zones = editedZones.split('\n').filter(zone => zone.trim() !== '');

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-2 py-4">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">배달 제외지역 설정</h1>
          <p className="text-sm text-gray-600">
            차량 배달 서비스가 불가능한 지역을 관리합니다. 각 지역은 엔터로 구분하여 입력하세요.
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">입력 방법</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>각 지역은 새로운 줄(엔터)로 구분하여 입력하세요</li>
                  <li>부산 외에 지역은 기본적으로 제외됩니다</li>
                  <li>고객이 선택한 주소가 이 목록의 지역으로 시작하면 배달 불가 처리됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              제외지역 목록
            </label>
            <textarea
              value={editedZones}
              onChange={handleTextChange}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              placeholder="부산 기장군&#10;부산 강서구&#10;경남"
            />

            {/* Preview Section */}
            {zones.length > 0 && (
              <div className="mt-3">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  현재 설정된 제외지역 ({zones.length}개)
                </div>
                <div className="flex flex-wrap gap-2">
                  {zones.map((zone, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800"
                    >
                      🚫 {zone}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-5 py-3 flex justify-end space-x-3 border-t">
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                hasChanges
                  ? 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
              }`}
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className={`px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors ${
                hasChanges && !updateMutation.isPending
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {updateMutation.isPending ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>

        {/* Last Updated Info */}
        {setting && (
          <div className="mt-3 text-xs text-gray-500 text-right">
            최종 수정: {new Date(setting.updated_at).toLocaleString('ko-KR')}
          </div>
        )}
      </div>
    </Layout>
  );
};
